import React, { type FunctionComponent, Fragment } from 'react'
import { css } from '@emotion/react'
import { useParams } from 'react-router'
import { spacing } from '@retailer-platform/shared-components'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { BannerBreadcrumbs } from 'domains/banners/components/banner-breadcrumbs/BannerBreadcrumbs'
import { BannerUpdateDetail } from 'domains/banners/components/banner-update/components/BannerUpdateDetail'
import {
  type BannerFormData,
  type FlyoutFormData,
  type ImageWithNullableDesktop,
} from 'domains/banners/types/banner.types'
import { BannerForm } from 'domains/banners/components/banner-form/BannerForm'
import { FlyoutForm } from 'domains/banners/components/banner-form/FlyoutForm'
import { type BannerQueryParams } from 'domains/banners/types/banner.types'
import { useQueryBanner } from 'domains/banners/utils/useQueryBanner.hooks'
import {
  ContentManagementAnnouncementBannerGenericImages,
  type ContentManagementFlyoutPlacement,
  type ContentManagementHeroBannerPlacement,
  type ContentManagementSecondaryBannerPlacement,
  PlacementDestinationTypeEnum,
} from 'domains/banners/api'
import {
  useUpdateFlyout,
  useUpdateHeroBanner,
  useUpdateSecondaryBanner,
} from 'domains/banners/api/updateBanner.hooks'
import {
  isHeroBanner,
  isSecondaryBanner,
  isFlyout,
  mapFormDataToFlyoutInput,
  mapFormDataToHeroBannerInput,
  mapFormDataToSecondaryBannerInput,
} from 'domains/banners/utils/bannerUtils'

const pageContainer = css({
  margin: `${spacing.X32} auto`,
  paddingLeft: spacing.X20,
  paddingRight: spacing.X20,
  maxWidth: '960px',
  width: '100%',
})

export const BannerUpdate: FunctionComponent = () => {
  const { placementId } = useParams<BannerQueryParams>()
  const goToList = useGoToPath('app-admin-banners-list')
  const {
    apiError,
    apiLoading,
    apiResult: unknownApiResult,
  } = useQueryBanner({
    placementId: placementId,
  })

  // ideally all these should be functions only executed once we know the type of banner
  const apiResult = unknownApiResult as
    | ContentManagementFlyoutPlacement
    | ContentManagementHeroBannerPlacement
    | ContentManagementSecondaryBannerPlacement
  const bannerApiResult = unknownApiResult as
    | ContentManagementHeroBannerPlacement
    | ContentManagementSecondaryBannerPlacement
  const flyoutApiResult = apiResult as ContentManagementFlyoutPlacement
  const images = apiResult?.images as ImageWithNullableDesktop

  const {
    updateFlyout,
    apiLoading: apiLoadingFlyout,
    apiError: apiErrorFlyout,
    apiSuccess: apiSuccessFlyout,
  } = useUpdateFlyout()

  const {
    updateHeroBanner,
    apiLoading: apiLoadingHeroBanner,
    apiError: apiErrorHeroBanner,
    apiSuccess: apiSuccessHeroBanner,
  } = useUpdateHeroBanner()

  const {
    updateSecondaryBanner,
    apiLoading: apiLoadingSecondaryBanner,
    apiError: apiErrorSecondaryBanner,
    apiSuccess: apiSuccessSecondaryBanner,
  } = useUpdateSecondaryBanner()

  const handleFlyoutFormSubmit = (formData: FlyoutFormData) => {
    updateFlyout({
      id: placementId,
      bannerId: flyoutApiResult.bannerId,
      bannerData: mapFormDataToFlyoutInput(formData),
    })
  }

  const handleHeroBannerFormSubmit = (formData: BannerFormData) => {
    updateHeroBanner({
      id: placementId,
      bannerId: bannerApiResult.bannerId,
      bannerData: mapFormDataToHeroBannerInput(formData),
    })
  }

  const handleSecondaryBannerFormSubmit = (formData: BannerFormData) => {
    updateSecondaryBanner({
      id: placementId,
      bannerId: bannerApiResult.bannerId,
      bannerData: mapFormDataToSecondaryBannerInput(formData),
    })
  }

  if (apiSuccessFlyout || apiSuccessHeroBanner || apiSuccessSecondaryBanner) goToList()
  if (apiLoading) return <LoadingPage />
  if (apiError) return <ErrorPage action={() => null} />

  const isBanner = isHeroBanner(apiResult.type) || isSecondaryBanner(apiResult.type)

  const parseDestinationExternal = (target: string) => {
    const jsonParsed = JSON.parse(target)
    return {
      url: jsonParsed['url'],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      open_in_new_tab: String(jsonParsed['openInNewTab']) || 'false',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      open_in_new_window: String(jsonParsed['openInNewWindow']) || 'false',
    }
  }

  const parseDestinationPickStoreselector = (target: string) => {
    const jsonParsed = JSON.parse(target)
    return {
      showDistance: String(jsonParsed['showDistance']),
    }
  }

  // this works on both banners and flyout results
  const buildDestination = (
    field: 'destination' | 'primaryDestination' | 'secondaryDestination' | 'linkDestination'
  ) => ({
    type: apiResult[field]?.type,
    target: apiResult[field]?.target,
    displayValue: apiResult[field]?.displayValue,
    external:
      apiResult[field]?.type === PlacementDestinationTypeEnum.External
        ? parseDestinationExternal(apiResult[field].target)
        : {
            url: '',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            open_in_new_tab: 'false',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            open_in_new_window: 'false',
          },
    storefront: {
      retailerId:
        apiResult[field]?.type === PlacementDestinationTypeEnum.Storefront
          ? apiResult[field].target
          : '',
    },
    storeselector: {
      retailerIds:
        apiResult[field]?.type === PlacementDestinationTypeEnum.StoreSelector
          ? apiResult[field].target
          : '',
    },
    pickupstoreselector:
      apiResult[field]?.type === PlacementDestinationTypeEnum.PickupStoreselector
        ? parseDestinationPickStoreselector(apiResult[field].target)
        : {
            showDistance: 'false',
          },
  })

  const initialStateBanner: BannerFormData = {
    ...bannerApiResult, // this is common placement properties
    bannerEnabled: apiResult.enabled ? 'true' : 'false',
    bannerRank: String(apiResult.rank),
    destination: isBanner ? buildDestination('destination') : null,
    images: {
      mobile: images.mobile,
      desktop: images.nullableDesktop,
    },
  }

  const initialStateFlyout = (): FlyoutFormData => ({
    ...flyoutApiResult, // this is common placement properties
    bannerEnabled: flyoutApiResult.enabled ? 'true' : 'false',
    bannerRank: String(flyoutApiResult.rank),
    primaryDestination: buildDestination('primaryDestination'),
    secondaryDestination: buildDestination('secondaryDestination'),
    linkDestination: buildDestination('linkDestination'),
    images: {
      mobile: images.mobile,
      desktop: images.nullableDesktop,
    },
  })

  const placementName = apiResult.name ? apiResult.name : placementId

  return (
    <Fragment>
      <BannerBreadcrumbs pageType="update" placementName={placementName} />
      <div css={pageContainer}>
        <BannerUpdateDetail id={apiResult.id} bannerId={apiResult.bannerId} />

        {isFlyout(apiResult.type) && (
          <FlyoutForm
            initialState={initialStateFlyout()}
            type={flyoutApiResult.type}
            onFormSubmit={handleFlyoutFormSubmit}
            submitError={apiErrorFlyout}
            isSubmitting={apiLoadingFlyout}
            onCancel={goToList}
          />
        )}

        {isHeroBanner(apiResult.type) && (
          <BannerForm
            initialState={initialStateBanner}
            type={apiResult.type}
            onFormSubmit={handleHeroBannerFormSubmit}
            submitError={apiErrorHeroBanner}
            isSubmitting={apiLoadingHeroBanner}
            onCancel={goToList}
          />
        )}
        {isSecondaryBanner(apiResult.type) && (
          <BannerForm
            initialState={initialStateBanner}
            type={apiResult.type}
            onFormSubmit={handleSecondaryBannerFormSubmit}
            submitError={apiErrorSecondaryBanner}
            isSubmitting={apiLoadingSecondaryBanner}
            onCancel={goToList}
          />
        )}
      </div>
    </Fragment>
  )
}
